import { updateDocument, createDocument, getId } from 'services/firestore'
import { uploadImage } from 'helpers'
import { updateParentHasManyRelationship } from 'hooks'
import { Design } from 'models'
import { message } from 'antd'
import { useHistory, useParams } from 'react-router-dom'
import { useState } from 'react'
import { useTranslations } from 'contexts/Translation'

const useActionsDesignAdvancedForm = ({ initialData, form } = {}) => {
  // [COMPONENT_STATE_HOOKS]
  const [loading, setLoading] = useState(false)

  // [ADDITIONAL_HOOKS]
  const { t } = useTranslations()
  const history = useHistory()
  const params = useParams()

  const prepareValues = async (values = {}, additionalValues = {}) => {
    const designId = initialData?.['design']?._id || getId('designs')
    const additionalReferences = { designId }
    const preparedValues = {
      _id: designId,
      ...additionalValues,
      description: values?.['design']?.['description'] ?? null,
      imageUrl: (await uploadImage(values?.['design']?.['imageUrl'])) ?? null,
      name: values?.['design']?.['name'] ?? null
    }
    if (params.customerProfileId)
      preparedValues['customerProfileId'] = params.customerProfileId
    return preparedValues
  }

  const saveForm = async (values, callback) => {
    try {
      // Prepare data to be saved
      const data = await prepareValues(values)
      // Updating parent references
      if (params.customerProfileId)
        await updateParentHasManyRelationship(
          params.customerProfileId,
          'customerProfiles',
          'designIds',
          data._id
        )
      // Save data
      if (initialData) {
        await updateDocument('designs', initialData.design?._id, data)
        message.success(t('Design successfully updated'))
      } else {
        await createDocument('designs', data, data._id)
        message.success(t('Design successfully created'))
      }
      // Final callback
      callback?.()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      throw new Error(t('Something went wrong during data save'))
    }
  }

  const validateForm = (values) => {
    try {
      // Prepare data to be validated
      const validationData = {
        description: values?.['design']?.['description'],
        imageUrl: values?.['design']?.['imageUrl'],
        name: values?.['design']?.['name']
      }
      Design.validationSchema.validateSync(validationData)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
      throw new Error(t('Design validation error: ') + t(error.message))
    }
  }

  const onFinish = async () => {
    if (loading) return // Avoid multiple calls

    try {
      setLoading(true)
      // Get form values
      const formValues = form.getFieldsValue()
      // Validate fields
      validateForm(formValues)
      // Final callback
      const callback = () => history.goBack()
      // Save data
      await saveForm(formValues, callback)
    } catch (error) {
      setLoading(false)
      message.error(error.message)
    }
  }

  const onReset = () => {
    form.resetFields()
    history.goBack()
  }

  return { onFinish, onReset, loading, saveForm, validateForm, prepareValues }
}

export default useActionsDesignAdvancedForm
